import React, { useState, useRef, useEffect } from 'react';
import { parseMarkdownToReact } from '../utils/markdownRenderer';
import { API_URL } from '../services/ApiService';

/**
 * MarkdownField - A simple field that accepts markdown input and renders it when not editing
 * 
 * @param {Object} props
 * @param {string} props.value - The field value (markdown)
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {string} props.type - 'input' or 'textarea'
 * @param {string} props.className - Custom CSS classes
 * @param {number} props.rows - Number of rows for textarea
 * @param {boolean} props.isEditing - External control of edit state
 * @param {Function} props.onBeginEdit - Called when editing begins
 * @param {Function} props.onEndEdit - Called when editing ends
 */
const MarkdownField = ({
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  type = 'input',
  className = '',
  rows = 3,
  isEditing: externalIsEditing,
  onBeginEdit,
  onEndEdit,
  ...props
}) => {
  // Use internal state if not controlled externally
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const fieldRef = useRef(null);
  
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  
  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  // Search users for mentions
  useEffect(() => {
    const searchUsers = async () => {
      if (mentionSearch.length < 1) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/users/search?q=${encodeURIComponent(mentionSearch)}&limit=10`,
          {
            credentials: 'include'
          }
        );
        
        if (response.ok) {
          const users = await response.json();
          setSuggestions(users || []);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [mentionSearch]);
  
  const handleBeginEdit = () => {
    if (disabled) return;
    
    // Make sure local value is synced when starting to edit
    setLocalValue(value || '');
    
    if (externalIsEditing === undefined) {
      setInternalIsEditing(true);
    }
    if (onBeginEdit) {
      onBeginEdit();
    }
  };
  
  const handleEndEdit = () => {
    // Close mentions
    setShowMentions(false);
    setMentionStartIndex(-1);
    setMentionSearch('');
    
    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
    if (onEndEdit) {
      onEndEdit();
    }
  };
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Check for @mentions
    const prevChar = cursorPos > 1 ? newValue[cursorPos - 2] : null;
    if (newValue[cursorPos - 1] === '@' && (cursorPos === 1 || [' ', '\n', '\t'].includes(prevChar))) {
      setMentionStartIndex(cursorPos - 1);
      setMentionSearch('');
      setShowMentions(true);
      setSelectedSuggestionIndex(0);
    } else if (mentionStartIndex >= 0) {
      const searchText = newValue.substring(mentionStartIndex + 1, cursorPos);
      
      if (searchText.includes(' ') || searchText.includes('\n') || !newValue.substring(mentionStartIndex).startsWith('@')) {
        setShowMentions(false);
        setMentionStartIndex(-1);
        setMentionSearch('');
      } else {
        setMentionSearch(searchText);
      }
    }
    
    setLocalValue(newValue);
    // Call onChange to update parent component
    onChange(newValue);
  };
  
  const handleKeyDown = (e) => {
    // Handle Cmd+K for links
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      
      const start = fieldRef.current.selectionStart;
      const end = fieldRef.current.selectionEnd;
      const selectedText = localValue.substring(start, end) || 'link';
      
      const url = window.prompt(`Enter URL for "${selectedText}":`, 'https://');
      if (url) {
        const beforeSelection = localValue.substring(0, start);
        const afterSelection = localValue.substring(end);
        const markdownLink = `[${selectedText}](${url})`;
        const newValue = beforeSelection + markdownLink + afterSelection;
        
        setLocalValue(newValue);
        onChange(newValue);
        
        // Focus back and set cursor after the inserted link
        setTimeout(() => {
          if (fieldRef.current) {
            const newCursorPos = beforeSelection.length + markdownLink.length;
            fieldRef.current.focus();
            fieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        }, 0);
      }
      return;
    }
    
    // Handle mention suggestions navigation
    if (showMentions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
          
        case 'Enter':
        case 'Tab':
          if (showMentions && suggestions[selectedSuggestionIndex]) {
            e.preventDefault();
            insertMention(suggestions[selectedSuggestionIndex]);
          }
          break;
          
        case 'Escape':
          if (showMentions) {
            setShowMentions(false);
            setMentionStartIndex(-1);
            setMentionSearch('');
          } else {
            handleEndEdit();
          }
          break;
          
        default:
          break;
      }
    } else if (e.key === 'Escape') {
      handleEndEdit();
    } else if (e.key === 'Enter' && type === 'input') {
      e.preventDefault();
      handleEndEdit();
    }
  };
  
  const insertMention = (user) => {
    const beforeMention = localValue.substring(0, mentionStartIndex);
    const afterMention = localValue.substring(mentionStartIndex + mentionSearch.length + 1);
    const newValue = `${beforeMention}@${user.username} ${afterMention}`;
    
    setLocalValue(newValue);
    onChange(newValue);
    
    // Reset mention state
    setShowMentions(false);
    setMentionStartIndex(-1);
    setMentionSearch('');
    setSelectedSuggestionIndex(0);
    
    // Focus back on field
    if (fieldRef.current) {
      const newCursorPos = beforeMention.length + user.username.length + 2;
      fieldRef.current.focus();
      fieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }
  };
  
  const defaultClassName = type === 'textarea'
    ? "w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-base placeholder-gray-400 resize-none"
    : "w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-base placeholder-gray-400";
  
  if (isEditing) {
    // Edit mode - show plain input/textarea
    return (
      <div className="relative">
        {type === 'textarea' ? (
          <textarea
            ref={fieldRef}
            className={className || defaultClassName}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleEndEdit}
            disabled={disabled}
            rows={rows}
            autoFocus
            {...props}
          />
        ) : (
          <input
            ref={fieldRef}
            className={className || defaultClassName}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleEndEdit}
            disabled={disabled}
            autoFocus
            {...props}
          />
        )}
        
        {/* Mention suggestions dropdown */}
        {showMentions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {suggestions.map((user, index) => (
              <div
                key={user._id}
                className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                  index === selectedSuggestionIndex 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'hover:bg-gray-50'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur
                  insertMention(user);
                }}
              >
                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium">
                  {(user.username?.[0] || user.email?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <div className="font-medium">{user.username || user.email}</div>
                  {user.firstName || user.lastName ? (
                    <div className="text-sm text-gray-500">
                      {user.firstName} {user.lastName}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Help text */}
        <div className="mt-1 text-xs text-gray-500">
          Cmd+K for links • @ to mention • Markdown supported
        </div>
      </div>
    );
  }
  
  // Display mode - render markdown
  const renderedContent = parseMarkdownToReact(value || '', {
    renderMentions: true,
    renderLinks: true,
    renderBold: true,
    renderItalic: true,
    renderStrike: true,
    renderLineBreaks: type === 'textarea'
  });
  
  return (
    <div
      className={`${className || defaultClassName} cursor-pointer ${
        !value ? 'text-gray-400' : ''
      }`}
      onClick={handleBeginEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBeginEdit();
        }
      }}
    >
      {renderedContent || <span className="text-gray-400">{placeholder}</span>}
    </div>
  );
};

export default MarkdownField;